import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useYupHelper from "@/features/ui/form/use-yup-helper";
import { useMemo } from "react";
import * as Yup from "yup";
import { Company, StoredObject } from "@/features/types";
import { PaymentMethod } from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice.type";
import { OrderConfirmationInvoiceFormFields } from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice-detail-page";
import useIdentity from "@/features/ui/hooks/use-identity";

// 브랜드별 금액·디파짓은 더 이상 입력하지 않는다(2026-10-30) — 총액·총 디파짓만 검사한다. 브랜드 행은 서버가 채운다.
export default function useOrderConfirmationInvoiceFormValidationSchema() {
  const { t } = useI18nHelper();
  const { withMessage } = useYupHelper({
    ns: "order_confirmation_invoices",
    prefix: "upload_order_confirmation_invoices_form",
  });

  const identity = useIdentity();

  return useMemo(() => {
    const isAgencySide = () => identity?.company?.type !== "BOUTIQUE";
    return Yup.object<OrderConfirmationInvoiceFormFields>({
      name: Yup.string().required(withMessage.required),
      season: Yup.string().required(withMessage.required),
      orderedBy: Yup.mixed<Company>().required(withMessage.required),
      issuedBy: Yup.mixed<Company>().required(withMessage.required),
      issuedThrough: Yup.mixed<Company>().when({
        is: () => {
          const type = identity?.company?.type;
          return type === "AGENCY" || type === "BUYER";
        },
        then: (schema) => schema.required(withMessage.required),
      }),
      issuedOn: Yup.date().required(withMessage.required),
      paymentMethod: Yup.mixed<PaymentMethod>().required(withMessage.required),
      exposedToBuyer: Yup.boolean().required(withMessage.required),
      paidByAgency: Yup.boolean().required(withMessage.required),
      // 인보이스 총액 — 담당자가 인보이스 금액을 그대로 넣는다
      totalAmount: Yup.object({
        value: Yup.number()
          .label("total_amount")
          .min(0, withMessage.min)
          .when({
            is: isAgencySide,
            then: (schema) => schema.required(withMessage.required),
          }),
        currency: Yup.string(),
      })
        .label("total_amount")
        .when({
          is: isAgencySide,
          then: (schema) => schema.required(withMessage.required),
        }),
      // 총 디파짓은 총액을 넘을 수 없다
      totalRequiredAmount: Yup.object({
        value: Yup.number()
          .label("total_deposit_required_amount")
          .min(0, withMessage.min)
          .when({
            is: isAgencySide,
            then: (schema) => schema.required(withMessage.required),
          }),
        currency: Yup.string(),
      })
        .label("total_deposit_required_amount")
        .when({
          is: isAgencySide,
          then: (schema) => schema.required(withMessage.required),
        })
        .test(
          "exceeding_required_deposit",
          t(
            "upload_order_confirmation_invoices_form.messages.error.exceeding_required_deposit",
            { ns: "order_confirmation_invoices" }
          ),
          (value, context) => {
            const { totalAmount } =
              context.parent as OrderConfirmationInvoiceFormFields;
            if (value?.value === undefined || totalAmount?.value === undefined) {
              return true;
            }
            return value.value <= totalAmount.value + 0.005;
          }
        ),
      file: Yup.mixed<File | Pick<StoredObject, "id" | "name">>()
        .label("invoice_file")
        .required(withMessage.required),
    });
  }, [withMessage.required, withMessage.min, t, identity?.company?.type]);
}
